<template>
  <v-card class="weather-widget-vertical" :class="containerSizeClass" elevation="0" ref="widgetRoot">

    <!-- ── Header con gradiente ── -->
    <div class="wxv-header" :style="headerBgStyle">
      <div class="wxv-overlay">

        <!-- Location + refresh -->
        <div class="wxv-topbar">
          <div class="wxv-location-row">
            <v-icon size="13" color="rgba(255,255,255,0.75)">mdi-map-marker</v-icon>
            <span class="wxv-location" v-if="!loadingWeather">{{ locationLabel }}</span>
            <v-skeleton-loader v-else type="text" width="70" dark />
          </div>
          <v-btn icon x-small @click="fetchWeather" :loading="loadingWeather">
            <v-icon size="14" color="rgba(255,255,255,0.75)">mdi-refresh</v-icon>
          </v-btn>
        </div>

        <!-- Temp + icon + stats -->
        <div class="wxv-main-row" v-if="!loadingWeather && weather">
					<div>
						<div class="wxv-temp-group">
							<div class="wxv-temp-line">
								<span class="wxv-temp">{{ weather.temp }}</span>
								<span class="wxv-temp-unit">°{{ weather.unit }}</span>
							</div>
							<div class="wxv-icon-center">
								<v-icon :size="56" :color="weather.iconColor" style="filter: drop-shadow(0 2px 8px rgba(0,0,0,0.3))">
									{{ weather.icon }}
								</v-icon>
							</div>
						</div>
						<span class="wxv-desc">{{ weather.description }}</span>
					</div>

          <div class="wxv-stats-col">
            <div class="wxv-stat">
              <v-icon size="13" color="rgba(255,255,255,0.65)">mdi-thermometer</v-icon>
              <span class="wxv-stat-lbl">Sensación</span>
              <span class="wxv-stat-val">{{ weather.feelsLike }}°</span>
            </div>
            <div class="wxv-stat">
              <v-icon size="13" color="rgba(255,255,255,0.65)">mdi-water-percent</v-icon>
              <span class="wxv-stat-lbl">Humedad</span>
              <span class="wxv-stat-val">{{ weather.humidity }}%</span>
            </div>
            <div class="wxv-stat">
              <v-icon size="13" color="rgba(255,255,255,0.65)">mdi-weather-windy</v-icon>
              <span class="wxv-stat-lbl">Viento</span>
              <span class="wxv-stat-val">{{ weather.windSpeed }} km/h</span>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div class="wxv-main-row" v-else-if="loadingWeather">
          <v-skeleton-loader type="heading" dark width="90" />
          <v-skeleton-loader type="avatar" dark width="56" height="56" />
          <v-skeleton-loader type="paragraph" dark width="90" />
        </div>

        <!-- Error -->
        <div v-if="error" class="wxv-error">
          <v-icon color="rgba(255,255,255,0.8)" size="28">mdi-weather-cloudy-alert</v-icon>
          <span>No se pudo obtener el clima</span>
        </div>

      </div>
    </div>

    <!-- ── Pronóstico ── -->
    <div class="wxv-forecast-section" v-if="!loadingWeather && weather && weather.forecast">

      <div class="wxv-forecast-label">
        <v-icon size="11" color="#BDBDBD">mdi-calendar-month-outline</v-icon>
        Pronóstico 14 días
      </div>

      <div class="wxv-day-list">
        <div
          v-for="(day, i) in weather.forecast"
          :key="i"
          class="wxv-day-item"
          :class="{ 'wxv-day-item--open': selectedForecastIndex === i }"
        >
          <!-- Fila principal -->
          <div class="wxv-day-row" @click="toggleForecastDay(i)">

            <div class="wxv-col-day">
              <span class="wxv-day-name">{{ day.day }}</span>
              <span class="wxv-day-date">{{ day.dateFormatted }}</span>
            </div>

            <div class="wxv-col-icon">
              <v-icon :size="20" :color="day.iconColor">{{ day.icon }}</v-icon>
            </div>

            <div class="wxv-col-bar">
              <span class="wxv-min-val">{{ day.min }}°</span>
              <div class="wxv-bar-track">
                <div class="wxv-bar-fill" :style="barStyle(day, weather.forecast)" />
              </div>
              <span class="wxv-max-val">{{ day.max }}°</span>
            </div>

            <v-icon size="14" class="wxv-chev" :class="{ 'wxv-chev--open': selectedForecastIndex === i }">
              mdi-chevron-down
            </v-icon>
          </div>

          <!-- Horas expandidas -->
          <div class="wxv-hours" v-show="selectedForecastIndex === i">
            <div v-for="(h, hi) in day.hours" :key="hi" class="wxv-hour">
              <span class="wxv-h-time">{{ formatHour(h.time) }}</span>
              <v-icon :size="16" :color="getIconByCode(h.condition.code, h.is_day === 0).color">
                {{ getIconByCode(h.condition.code, h.is_day === 0).icon }}
              </v-icon>
              <span class="wxv-h-temp text-center">{{ Math.round(h.temp_c) }}°</span>
              <div class="wxv-h-precip" v-if="h.chance_of_rain > 0 || h.chance_of_snow > 0">
                <v-icon size="11" color="#90CAF9">mdi-water-outline</v-icon>
                <span>{{ h.chance_of_rain || h.chance_of_snow }}%</span>
              </div>
              <span class="wxv-h-cond">{{ h.condition.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </v-card>
</template>

<script>
import { WEATHER_ICONS_BY_CODE, DAY_LABELS } from '@/helpers/constants/weather'

export default {
  name: 'WeatherWidgetVertical',

  props: {
    lat:          { type: Number, default: null },
    lon:          { type: Number, default: null },
    locationName: { type: String, default: null }
  },

  data: () => ({
    weather:               null,
    loadingWeather:        false,
    error:                 false,
    detectedLocation:      null,
    selectedForecastIndex: null,
    widgetWidth:           0
  }),

  computed: {
    selectedProject () {
      return this.$store.projectsStore.state.selectedProject
    },
    locationLabel () {
      return this.locationName || this.detectedLocation || 'Mi ubicación'
    },
    headerBgStyle () {
      const bg = this.weather?.bg || ['#546E7A', '#37474F']
      return { background: `linear-gradient(150deg, ${bg[0]} 0%, ${bg[1]} 100%)` }
    },
    // Clases responsivas basadas en el ancho del contenedor
    containerSizeClass () {
      const w = this.widgetWidth
      if (w === 0) return 'wxv-size-md' // default
      if (w < 220) return 'wxv-size-xs'  // muy angosto
      if (w < 280) return 'wxv-size-sm'  // angosto
      if (w < 380) return 'wxv-size-md'  // medio
      return 'wxv-size-lg'               // amplio
    }
  },

  watch: {
    selectedProject (newVal, oldVal) {
      if (newVal && newVal !== oldVal) this.fetchWeather()
    }
  },

  mounted () {
    if (this.selectedProject) this.fetchWeather()
    this._ro = new ResizeObserver(entries => {
      this.widgetWidth = entries[0]?.contentRect.width ?? 0
    })
    if (this.$refs.widgetRoot?.$el) this._ro.observe(this.$refs.widgetRoot.$el)
  },

  beforeDestroy () {
    this._ro?.disconnect()
  },

  methods: {
    async fetchWeather () {
      this.loadingWeather = true
      this.error = false
      try {
				let lat = this.selectedProject?.geo?.latitude
				let lon = this.selectedProject?.geo?.longitude
				if(!lat || !lon) {
					({ lat, lon } = await this.getCoords())
				}
        const apiKey = process.env.VUE_APP_OPENWEATHER_API_KEY || 'ea3a7b1921494a81a9e52003261404'
				if(!lat || !lon || !apiKey) {
					return null
				}
        const url    = `https://api.weatherapi.com/v1/forecast.json?key=${apiKey}&q=${lat},${lon}&days=14&lang=es`
        const res    = await fetch(url)
        const data   = await res.json()
        this.weather = this.parseWeatherData(data)
        if (!this.locationName) this.detectedLocation = data.location?.name || null
      } catch (e) {
        console.error('Weather error:', e)
        this.error   = true
        this.weather = null
      } finally {
        this.loadingWeather = false
      }
    },

    parseWeatherData (data) {
      const cur  = data.current
      const meta = this.getIconByCode(cur.condition.code, cur.is_day === 0)

      const forecast = data.forecast.forecastday.slice(0, 14).map(day => {
        const date      = new Date(day.date + 'T12:00:00')
        const dm        = this.getIconByCode(day.day.condition.code, false)
        const [, m, d]  = day.date.split('-')
        return {
          day:           DAY_LABELS[date.getDay()],
          dateFormatted: `${d}/${m}`,
          max:           Math.round(day.day.maxtemp_c),
          min:           Math.round(day.day.mintemp_c),
          icon:          dm.icon,
          iconColor:     dm.color,
          hours:         day.hour || []
        }
      })

      return {
        temp:        Math.round(cur.temp_c),
        feelsLike:   Math.round(cur.feelslike_c),
        humidity:    cur.humidity,
        windSpeed:   Math.round(cur.wind_kph),
        description: this.capitalize(cur.condition.text),
        unit:        'C',
        icon:        meta.icon,
        iconColor:   meta.color,
        bg:          meta.bg,
        forecast
      }
    },

    toggleForecastDay (i) {
      this.selectedForecastIndex = this.selectedForecastIndex === i ? null : i
    },

    formatHour (t) {
      return t ? t.split(' ')[1] : ''
    },

    getIconByCode (code, isNight) {
      if (isNight && code === 1000)
        return { icon: 'mdi-weather-night', color: '#90CAF9', bg: ['#1A237E', '#0D47A1'] }
      return WEATHER_ICONS_BY_CODE[code] || WEATHER_ICONS_BY_CODE.default
    },

    capitalize (s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
    },

    barStyle (day, forecast) {
      const allMin = Math.min(...forecast.map(d => d.min))
      const allMax = Math.max(...forecast.map(d => d.max))
      const range  = allMax - allMin || 1
      const left   = ((day.min - allMin) / range) * 100
      const width  = Math.max(10, ((day.max - day.min) / range) * 100)
      return { left: `${left}%`, width: `${width}%` }
    }
  }
}
</script>

<style scoped>
/* ── Tarjeta base ─────────────────────────────────────── */
.weather-widget-vertical {
  border-radius: 20px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5 !important;
  border: 1px solid rgba(0,0,0,0.06) !important;
}

/* ── Header ───────────────────────────────────────────── */
.wxv-header { flex-shrink: 0; transition: background 0.5s; }
.wxv-overlay {
  padding: 14px 16px 20px;
  background: linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.22) 100%);
}

.wxv-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.wxv-location-row { display: flex; align-items: center; gap: 4px; }
.wxv-location {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.85);
}

.wxv-main-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.wxv-temp-group { display: flex; flex: 1; min-width: 0; gap: 5px; }
.wxv-temp-line  { display: flex; align-items: flex-end; gap: 2px; }
.wxv-temp       { font-size: 52px; font-weight: 200; color: #fff; line-height: 1; letter-spacing: -3px; }
.wxv-temp-unit  { font-size: 22px; font-weight: 300; color: rgba(255,255,255,0.85); margin-bottom: 8px; }
.wxv-desc       { font-size: 12px; color: rgba(255,255,255,0.78); margin-top: 4px; display: block; }

.wxv-icon-center { flex-shrink: 0; }

.wxv-stats-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(0,0,0,0.15);
  border-radius: 12px;
  padding: 9px 11px;
}
.wxv-stat     { display: flex; align-items: center; gap: 5px; }
.wxv-stat-lbl { font-size: 10px; color: rgba(255,255,255,0.7); flex: 1; }
.wxv-stat-val { font-size: 11px; font-weight: 700; color: #fff; white-space: nowrap; }

.wxv-error { display: flex; flex-direction: column; align-items: center; gap: 4px; margin-top: 12px; }
.wxv-error span { font-size: 12px; color: rgba(255,255,255,0.8); }

/* ── Sección pronóstico ───────────────────────────────── */
.wxv-forecast-section {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0 12px;
  background: #F5F5F5;
}

.wxv-forecast-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #BDBDBD;
  padding: 0 14px 8px;
}

/* Lista de días — siempre una columna, ancho completo */
.wxv-day-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 10px;
}

.wxv-day-item {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.05);
  transition: box-shadow 0.15s;
}
.wxv-day-item:hover        { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.wxv-day-item--open        { background: #FAFCFD; }

/* Fila del día */
.wxv-day-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  cursor: pointer;
  min-height: 46px;
}

/* Día + fecha: ancho fijo para alinear columnas */
.wxv-col-day {
  display: flex;
  flex-direction: column;
  width: 52px;
  flex-shrink: 0;
}
.wxv-day-name {
  font-size: 12px;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
}
.wxv-day-date {
  font-size: 10px;
  color: #9E9E9E;
  margin-top: 1px;
}

/* Ícono: ancho fijo */
.wxv-col-icon {
  width: 26px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}

/* Barra temperatura: ocupa el espacio restante */
.wxv-col-bar {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.wxv-min-val {
  font-size: 11px;
  color: #90A4AE;
  width: 26px;
  text-align: right;
  flex-shrink: 0;
}
.wxv-max-val {
  font-size: 11px;
  font-weight: 700;
  color: #E65100;
  width: 26px;
  flex-shrink: 0;
}
.wxv-bar-track {
  flex: 1;
  height: 5px;
  background: #ECEFF1;
  border-radius: 3px;
  position: relative;
  min-width: 20px;
}
.wxv-bar-fill {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, #64B5F6 0%, #FF8A65 100%);
}

/* Chevron */
.wxv-chev {
  flex-shrink: 0;
  color: #BDBDBD !important;
  transition: transform 0.2s ease;
}
.wxv-chev--open {
  transform: rotate(180deg);
  color: #78909C !important;
}

/* ── Horas expandidas ─────────────────────────────────── */
.wxv-hours {
  border-top: 1px solid #F0F0F0;
  background: #FAFAFA;
  padding: 4px 12px 8px;
}
.wxv-hour {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0,0,0,0.03);
}
.wxv-hour:last-child { border-bottom: none; }

.wxv-h-time   { font-size: 11px; color: #9E9E9E; width: 36px; flex-shrink: 0; }
.wxv-h-temp   { font-size: 12px; font-weight: 700; color: #424242; width: 28px; flex-shrink: 0; }
.wxv-h-precip {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: #64B5F6;
  flex-shrink: 0;
}
.wxv-h-cond {
  font-size: 11px;
  color: #757575;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── RESPONSIVIDAD BASADA EN ANCHO DEL CONTENEDOR ────── */

/* Extra pequeño: < 220px - Layout ultra-compacto */
.wxv-size-xs .wxv-main-row {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}
.wxv-size-xs .wxv-stats-col {
  flex-direction: row;
  justify-content: space-around;
  background: rgba(0,0,0,0.1);
  padding: 6px 8px;
  gap: 8px;
}
.wxv-size-xs .wxv-stat-lbl {
  display: none;
}
.wxv-size-xs .wxv-stat {
  flex-direction: column;
  gap: 2px;
  align-items: center;
}
.wxv-size-xs .wxv-stat-val {
  font-size: 10px;
}
.wxv-size-xs .wxv-temp {
  font-size: 38px;
  letter-spacing: -2px;
}
.wxv-size-xs .wxv-temp-unit {
  font-size: 18px;
}
.wxv-size-xs .wxv-desc {
  font-size: 10px;
}
.wxv-size-xs .wxv-icon-center .v-icon {
  font-size: 40px !important;
}
.wxv-size-xs .wxv-col-day {
  width: 38px;
}
.wxv-size-xs .wxv-day-name {
  font-size: 10px;
}
.wxv-size-xs .wxv-day-date {
  font-size: 9px;
}
.wxv-size-xs .wxv-day-row {
  gap: 4px;
  flex-wrap: wrap;
}
.wxv-size-xs .wxv-col-bar {
  gap: 4px;
}
.wxv-size-xs .wxv-min-val,
.wxv-size-xs .wxv-max-val {
  width: 20px;
  font-size: 10px;
}
.wxv-size-xs .wxv-chev {
  order: 3;
  width: 100%;
  text-align: center;
  margin-top: -4px;
}
.wxv-size-xs .wxv-hour {
  flex-wrap: wrap;
	justify-content: space-between;
  gap: 4px;
}
.wxv-size-xs .wxv-h-cond {
  width: 100%;
  margin-top: 2px;
  white-space: normal;
  font-size: 10px;
}

/* Pequeño: < 280px - Stats abajo de la temperatura */
.wxv-size-sm .wxv-main-row {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}
.wxv-size-sm .wxv-stats-col {
  flex-direction: row;
  justify-content: space-around;
  padding: 6px 8px;
  gap: 8px;
}
.wxv-size-sm .wxv-stat-lbl {
  display: none;
}
.wxv-size-sm .wxv-stat {
  flex-direction: column;
  gap: 2px;
  align-items: center;
}
.wxv-size-sm .wxv-stat-val {
  font-size: 10px;
}
.wxv-size-sm .wxv-temp {
  font-size: 44px;
  letter-spacing: -2.5px;
}
.wxv-size-sm .wxv-temp-unit {
  font-size: 20px;
}
.wxv-size-sm .wxv-desc {
  font-size: 11px;
}
.wxv-size-sm .wxv-icon-center .v-icon {
  font-size: 48px !important;
}
.wxv-size-sm .wxv-col-day {
  width: 44px;
}
.wxv-size-sm .wxv-day-row {
  gap: 5px;
}
.wxv-size-sm .wxv-col-bar {
  gap: 4px;
}
.wxv-size-sm .wxv-min-val,
.wxv-size-sm .wxv-max-val {
  width: 22px;
  font-size: 10px;
}
.wxv-size-sm .wxv-hour {
  flex-wrap: wrap;
  gap: 4px;
}
.wxv-size-sm .wxv-h-cond {
  width: 100%;
  margin-top: 2px;
  white-space: normal;
  font-size: 10px;
}

/* Mediano: < 380px - Layout estándar compacto */
.wxv-size-md .wxv-temp {
  font-size: 48px;
}
.wxv-size-md .wxv-temp-unit {
  font-size: 20px;
}
.wxv-size-md .wxv-icon-center .v-icon {
  font-size: 50px !important;
}
.wxv-size-md .wxv-day-row {
  gap: 6px;
}
.wxv-size-md .wxv-col-bar {
  gap: 5px;
}

/* Grande: >= 380px - Layout completo (estilos por defecto) */
/* No necesita override, usa los estilos base */
</style>